import { AbstractControl } from '@angular/forms';
import {
  CardinalDirection,
  Coordinate,
  CoordinateKind,
  isCardinalDirection,
} from './coordinate.model.v2';

export enum CoordinateFormat {
  DegreesDecimalMinutes = 'ddm',
  DegreesMinutesSeconds = 'dms',
  DecimalDegrees = 'dd',
}

export interface ParsedCoordinateValues {
  value: number;
  degrees: number;
  minutes: number;
  seconds: number;
  direction: CardinalDirection;
}

export function parseInput(input: string | number, kind: CoordinateKind): ParsedCoordinateValues {
  if (typeof input === 'string' && !isNaN(Number(input)) && input.includes('.')) {
    // For the current functionality, we are assuming all latitude coordinates are
    // in the northern hemisphere, and all longitude coordinates are in the western hemisphere
    const direction: CardinalDirection = kind === CoordinateKind.Latitude ? 'N' : 'W';
    // We're also going to coerce any negative values to positive values, because we're already
    // providing the cardinality
    const decimalDegrees = Math.abs(Number(input));
    const coord = Coordinate.create({ decimalDegrees, direction });
    return coord;
  }

  if (typeof input === 'string') {
    const parsedDms = parseDegreesMinutesSeconds(input, kind);
    const parsedDisplay = parseDisplayValue(parsedDms, kind);
    return parsedDisplay;
  }
  const parsedDecimal = parseDecimalCoordinate(input, kind);
  return parsedDecimal;
}

function validateRange(val: number, min: number, max: number, type: 'deg' | 'mins') {
  if (val < min || val > max) {
    return `Out of ${min} to ${max} ${type}`;
  }

  return null;
}

export function validateLongitudeRange(val: number) {
  return validateRange(val, -180, 180, 'deg');
}

export function validateLatitudeRange(val: number) {
  return validateRange(val, -90, 90, 'deg');
}

export function validateMinutesRange(val: number) {
  return validateRange(val, 0, 60, 'mins');
}

export function getParsedCoordinates(control: AbstractControl, kind: CoordinateKind) {
  const input = control.value;
  if (input != null) {
    return parseInput(input, kind);
  }

  return null;
}

export function ensureValidCoordinates(
  val: number,
  minuteSeconds: number,
  direction: CardinalDirection,
  kind: CoordinateKind,
): string[] | null {
  let expectedValue = '';
  const errors: string[] = [];

  switch (kind) {
    case CoordinateKind.Latitude:
      addIfExists(validateLatitudeRange(val), errors);
      expectedValue = "'N' or 'S'";
      break;
    case CoordinateKind.Longitude:
      addIfExists(validateLongitudeRange(val), errors);
      expectedValue = "'E' or 'W'";
      break;
  }

  // validate minutes
  addIfExists(validateRange(minuteSeconds, 0, 60, 'mins'), errors);

  // cardinal direction must match the coordinate kind.
  if (kind !== getCoordinateKind(direction)) {
    errors.push(
      `Cardinal direction '${direction}' does not match Coordinate kind '${kind}'. Expecting ${expectedValue}.`,
    );
  }

  return errors;
}

function addIfExists(err: string | null, errors: string[]) {
  if (err != null) {
    errors.push(err);
  }
}

export function parseDegreesMinutesSeconds(val: string, kind: CoordinateKind): string {
  const len = (v: string | null): number => (v == null ? 0 : v.length);
  const pad = (input: string | null, cnt: number = 2) =>
    (input == null ? '' : input).padStart(cnt, '0');

  let degPad = 2;
  let [deg, min, sec] = ['', '', ''];
  val = val.replace(/[^0-9]/g, '');

  if (kind === CoordinateKind.Longitude) {
    // length for longitude can be 6 or 7. allows for missing zero. ie: 0814532 or 814532
    const cnt = len(val) < 7 ? 2 : 3;
    deg = val.substr(0, cnt);
    min = val.substr(deg.length, 2);
    sec = val.substr(deg.length + min.length);
    degPad = 3;
  } else {
    deg = val.substr(0, 2);
    min = val.substr(deg.length, 2);
    sec = val.substr(deg.length + min.length);
  }

  const direction = kind === CoordinateKind.Latitude ? 'N' : 'W';

  const result = `${direction} ${pad(deg, degPad)}:${pad(min)}.${pad(sec)}`;
  return result;
}

function parseDisplayValue(inputVal: string, kind: CoordinateKind): ParsedCoordinateValues {
  // clean the string of all non parsable characters
  const degreesMinutes = cleanCoordinateString(inputVal);
  const pattern =
    /^((([NSWE]) ?)?(\d{1,3}) (\d{1,2}) (\d{1,2}\.\d{1,3}|\d{1,2})|(([NSWE]) ?)?(\d{1,3}) (\d{1,2}\.\d{1,3}|\d{1,2}))$/i;
  const matches = degreesMinutes.match(pattern);

  if (matches == null || matches.length < 5) {
    throw Error('parseStringCoordinate: unable to find pattern match.');
  }

  // get values from matches
  const [, , , directionMatch, degrees, minutes, seconds] =
    matches[3] == null ? matches.slice(5) : matches;

  // set parts of coordinate string
  let deg = parseInt(degrees, 10);
  const minutesSeconds = parseMinutes(minutes, seconds);
  if (isNaN(deg)) {
    throw new Error(`parseStringCoordinate: the value for deg '${deg}' is not a number.`);
  }
  if (isNaN(minutesSeconds)) {
    throw new Error(
      `parseStringCoordinate: the value for minutesSeconds '${minutesSeconds}' is not a number.`,
    );
  }

  const direction: CardinalDirection | undefined = isCardinalDirection(directionMatch)
    ? directionMatch
    : getCardinalDirection(deg, kind);

  const multiplier = getMultiplier(direction);

  deg = deg * (deg < 0 ? -1 : 1);
  const sec = (minutesSeconds - Math.trunc(minutesSeconds)) * 60;

  const val = (deg + minutesSeconds / 60) * multiplier;

  // set values
  return {
    value: val,
    degrees: deg,
    minutes: Math.trunc(minutesSeconds),
    seconds: sec,
    direction,
  };
}

export class CoordinateV1 {
  private _value: number | null = null;
  private _direction: CardinalDirection | null = null;
  private _degrees = 0;
  private _minutes = 0;
  private _seconds = 0;
  private _roundTo = 2;

  get direction(): CardinalDirection | null {
    return this._direction;
  }
  get degrees(): number {
    return this._degrees;
  }
  get minutes(): number {
    return this._minutes;
  }
  get seconds(): number {
    return this._seconds;
  }
  get value(): number | null {
    return this._value;
  }

  constructor(inputVal: string | number | null, public readonly kind: CoordinateKind) {
    let coord: ParsedCoordinateValues | null = null;
    if ((inputVal == null || typeof inputVal === 'number') && kind == null) {
      throw new Error('Coordinate: kind is required when passing a decimal or null value.');
    }

    inputVal =
      typeof inputVal === 'string' && !isNaN(parseFloat(inputVal))
        ? parseFloat(inputVal)
        : inputVal;

    if (typeof inputVal === 'number') {
      coord = parseDecimalCoordinate(inputVal, kind);
    } else if (typeof inputVal === 'string') {
      if (kind == null) {
        const match = inputVal.match(/^([NSWE])/);
        if (match != null) {
          kind = getCoordinateKind(match[1] as CardinalDirection);
        }
      }
      coord = parseDisplayValue(inputVal, kind);
    }

    if (coord != null) {
      // if invalid throw error
      const errors = ensureValidCoordinates(
        coord.value,
        coord.minutes + coord.seconds / 60,
        coord.direction,
        kind,
      );
      if (errors != null && errors.length > 0) {
        errors.forEach((e) => {
          throw new Error(e);
        });
      }

      this._value = coord.value;
      this._degrees = coord.degrees;
      this._minutes = coord.minutes;
      this._seconds = coord.seconds;
      this._direction = coord.direction;
    } else {
      this._direction = getCardinalDirection(0, kind);
    }
  }

  public toString(format: CoordinateFormat = CoordinateFormat.DegreesDecimalMinutes): string {
    let rMinutes = (this.minutes + this.seconds / 60).toFixed(this._roundTo);
    const degPad = this.kind === CoordinateKind.Longitude ? 3 : 2;
    switch (format) {
      case CoordinateFormat.DegreesMinutesSeconds:
        return `${this.direction} ${padNum(this.degrees, degPad)} ${padNum(this.minutes)} ${padNum(
          Math.round(this.seconds),
        )}`;
      case CoordinateFormat.DegreesDecimalMinutes:
        rMinutes = rMinutes.indexOf('.') === 1 ? `0${rMinutes}` : rMinutes;
        return `${this.direction} ${padNum(this.degrees, degPad)}:${rMinutes}`;
      default:
        throw new Error(`Unknown coordinate string format '${format}'`);
    }
  }
}

function padNum(val: number, length: number = 2) {
  return String(val).padStart(length, '0');
}

function parseDecimalCoordinate(val: number, kind: CoordinateKind): ParsedCoordinateValues {
  const direction = getCardinalDirection(val, kind);
  let [deg, min, sec] = [0, 0, 0];
  if (val !== 0) {
    const multiplier = getMultiplier(direction);
    deg = Math.trunc(val);
    min = Math.trunc((val - deg) * 60 * multiplier);
    sec = ((val - deg) * 60 - Math.trunc((val - deg) * 60)) * 60 * multiplier;

    // ensure positive degrees.
    deg = deg * multiplier;

    // if invalid throw exception
    const errors = ensureValidCoordinates(val, min + sec / 60, direction, kind);
    if (errors != null && errors.length > 0) {
      errors.forEach((e) => {
        throw new Error('parseDecimalCoordinate: ' + e);
      });
    }
  }

  return {
    value: val,
    degrees: deg,
    minutes: min,
    seconds: sec,
    direction,
  };
}

function parseMinutes(minutes: string, seconds: string): number {
  let min = parseFloat(minutes);

  if (seconds != null) {
    if (min - Math.trunc(min) !== 0) {
      throw new Error(
        'parseMinutes: Unable to parse Minutes and Seconds. Minutes must be an integer when parsing a Degrees, Minutes, Seconds string.',
      );
    }
    min += parseFloat(seconds) / 60;
  }

  return min;
}

function cleanCoordinateString(inputVal: string) {
  const val = inputVal.replace(/[^ NEWS0-9\-\.]/g, ' ');
  return val.replace(/ +/, ' ').trim();
}

function getCardinalDirection(degrees: number, kind: CoordinateKind): CardinalDirection {
  switch (kind) {
    case CoordinateKind.Latitude:
      return degrees < 0 ? 'S' : 'N';
    case CoordinateKind.Longitude:
      return degrees < 0 ? 'W' : 'E';
  }
}

function getMultiplier(direction: CardinalDirection): number {
  switch (getCoordinateKind(direction)) {
    case CoordinateKind.Latitude:
      return direction === 'S' ? -1 : 1;
    case CoordinateKind.Longitude:
      return direction === 'W' ? -1 : 1;
    default:
      return 0;
  }
}

function getCoordinateKind(direction: CardinalDirection): CoordinateKind {
  switch (direction) {
    case 'N':
    case 'S':
      return CoordinateKind.Latitude;
    case 'W':
    case 'E':
      return CoordinateKind.Longitude;
    default:
      throw new Error(
        `getCoordinateDirection: unable to get cooridnate kind from cardinal value '${direction}'. Must be one of the following: N,S,E,W.`,
      );
  }
}
